import { ACCENT_PRESETS, isHexColor } from '../lib/accent';
import { contrastRatio } from '../lib/contrast';
import {
  applyDesign,
  applyDesignAccent,
  applyDesignMode,
  applyDesignPalette,
  DESIGN_PALETTES,
  DESIGN_PRESETS,
  useDesign,
  useDesignAccent,
  useDesignMode,
  useDesignPalette,
  type DesignMode,
} from '../lib/design';

const MODES: ReadonlyArray<{ mode: DesignMode; label: string }> = [
  { mode: 'dark', label: 'Dark' },
  { mode: 'light', label: 'Light' },
];

/** The darkest on-accent ink any design uses (Toy Box's `--accent-contrast`); Ledger's is darker. */
const ON_ACCENT_INK = '#1d1a2b';
const AA = 4.5;

const swatchButton = (isSelected: boolean) =>
  [
    'flex min-h-tap min-w-tap items-center justify-center rounded-full border-2 p-1 transition-transform hover:scale-105',
    isSelected ? 'border-text' : 'border-transparent',
  ].join(' ');

/**
 * The A/B/C design picker (REV30 — docs/specs/REV30-design-variants.md), applied live via
 * `applyDesign` (sets/removes `data-design` on `<html>`, persists to localStorage, and notifies
 * `useDesign` so `DesignShell` swaps the shell without a reload). Same radiogroup idiom as
 * /settings' theme cards; the letter is shown so feedback can say "B" and mean one thing.
 */
export function DesignPicker() {
  const selected = useDesign();
  const mode = useDesignMode();
  const palette = useDesignPalette();
  const accent = useDesignAccent();

  const activePalette = selected != null ? DESIGN_PALETTES[selected].find((p) => p.slug === palette) : undefined;
  const paletteAccent = activePalette && mode != null ? activePalette.swatch[mode].accent : null;
  const isPresetAccent = accent != null && ACCENT_PRESETS.some((preset) => preset.color === accent);
  // Text on accent fills is always a near-black ink in every design, so one reference is enough.
  const accentContrast = accent != null ? contrastRatio(accent, ON_ACCENT_INK) : null;

  return (
    <section className="rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
      <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-text-faint">Design</h2>
      <p className="mt-1 max-w-prose text-sm text-text-muted">
        Three looks, same app — everything works the same in all of them. Pick whichever you like;
        it's saved on this device.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Design">
        {DESIGN_PRESETS.map((preset) => {
          const isSelected = preset.slug === selected;
          return (
            <button
              key={preset.letter}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => applyDesign(preset.slug)}
              className={[
                'flex min-h-tap items-center gap-3 rounded-lg border-2 p-2.5 text-left transition-colors',
                isSelected ? 'border-accent bg-accent-dim' : 'border-border hover:border-border-strong',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="relative flex size-12 shrink-0 flex-col justify-end gap-1 overflow-hidden rounded-md border border-border-strong/40 p-1.5"
                style={{ backgroundColor: preset.swatch.bg }}
              >
                <span className="block h-2.5 w-full rounded-sm" style={{ backgroundColor: preset.swatch.elevated }} />
                <span className="block h-1 w-2/3 rounded-sm" style={{ backgroundColor: preset.swatch.text }} />
                <span
                  className="absolute right-1.5 top-1.5 size-2.5 rounded-full"
                  style={{ backgroundColor: preset.swatch.accent }}
                />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-text">
                  <span className="mr-1.5 font-display text-text-faint">{preset.letter}</span>
                  {preset.name}
                </span>
                <span className="block text-xs text-text-muted">{preset.note}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Classic has the theme cards below instead; B/C come in two modes × a few palettes. */}
      {selected != null && (
        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm font-semibold text-text-muted">Mode</span>
          <div className="flex gap-2" role="radiogroup" aria-label="Design mode">
            {MODES.map((option) => {
              const isSelected = option.mode === mode;
              return (
                <button
                  key={option.mode}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => applyDesignMode(option.mode)}
                  className={[
                    'min-h-tap min-w-20 rounded-lg border-2 px-4 text-sm font-semibold transition-colors',
                    isSelected
                      ? 'border-accent bg-accent-dim text-text'
                      : 'border-border text-text-muted hover:border-border-strong hover:text-text',
                  ].join(' ')}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Colourways of the active design. Swatches follow the current mode, so the card previews
          exactly what a click will paint. */}
      {selected != null && mode != null && (
        <div className="mt-4 flex flex-wrap gap-3" role="radiogroup" aria-label="Palette">
          {DESIGN_PALETTES[selected].map((option) => {
            const isSelected = option.slug === palette;
            const swatch = option.swatch[mode];
            return (
              <button
                key={option.name}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => applyDesignPalette(option.slug)}
                className={[
                  'flex min-h-tap w-[6.5rem] flex-col items-center gap-1.5 rounded-lg border-2 p-2 transition-colors',
                  isSelected ? 'border-accent bg-accent-dim' : 'border-transparent hover:border-border-strong',
                ].join(' ')}
              >
                <span
                  aria-hidden="true"
                  className="relative flex h-12 w-full flex-col justify-end gap-0.5 overflow-hidden rounded-md border border-border-strong/40 p-1.5"
                  style={{ backgroundColor: swatch.bg }}
                >
                  <span className="block h-2.5 w-3/4 rounded-sm" style={{ backgroundColor: swatch.elevated }} />
                  <span className="block h-1 w-1/2 rounded-sm" style={{ backgroundColor: swatch.text }} />
                  <span
                    className="absolute right-1.5 top-1.5 size-2.5 rounded-full"
                    style={{ backgroundColor: swatch.accent }}
                  />
                </span>
                <span className="text-xs font-semibold text-text">{option.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Per-design accent (lib/design.ts): first swatch hands the colour back to the palette. */}
      {selected != null && paletteAccent != null && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-text-muted">Accent</p>
          <div className="mt-2 flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Design accent colour">
            <button
              type="button"
              role="radio"
              aria-checked={accent == null}
              aria-label="Palette default"
              title="Palette default"
              onClick={() => applyDesignAccent(null)}
              className={swatchButton(accent == null)}
            >
              <span
                aria-hidden="true"
                className="flex size-8 items-center justify-center rounded-full text-[0.6rem] font-bold shadow-inner"
                style={{ backgroundColor: paletteAccent, color: ON_ACCENT_INK }}
              >
                ★
              </span>
            </button>
            {ACCENT_PRESETS.map((preset) => (
              <button
                key={preset.color}
                type="button"
                role="radio"
                aria-checked={preset.color === accent}
                aria-label={preset.name}
                title={preset.name}
                onClick={() => applyDesignAccent(preset.color)}
                className={swatchButton(preset.color === accent)}
              >
                <span
                  aria-hidden="true"
                  className="size-8 rounded-full shadow-inner"
                  style={{ backgroundColor: preset.color }}
                />
              </button>
            ))}
            <label className={[swatchButton(accent != null && !isPresetAccent), 'cursor-pointer'].join(' ')} title="Custom…">
              <span
                aria-hidden="true"
                className="relative flex size-8 items-center justify-center rounded-full shadow-inner"
                style={{
                  backgroundColor: accent != null && !isPresetAccent ? accent : undefined,
                  backgroundImage:
                    accent != null && !isPresetAccent
                      ? undefined
                      : 'conic-gradient(from 0deg, #ff5d47, #e8c13a, #9ed44a, #3ecfd8, #5fb4ff, #b18cff, #ff5fb8, #ff5d47)',
                }}
              >
                {(accent == null || isPresetAccent) && (
                  <span className="text-[0.6rem] font-bold text-white drop-shadow">+</span>
                )}
              </span>
              <input
                type="color"
                aria-label="Custom design accent colour"
                value={accent ?? paletteAccent}
                onChange={(event) => {
                  if (isHexColor(event.target.value)) applyDesignAccent(event.target.value);
                }}
                className="sr-only"
              />
            </label>
            <span className="text-xs font-medium text-text-muted">Custom…</span>
          </div>
          {accentContrast != null && accentContrast < AA && (
            <p className="mt-2 max-w-prose text-sm text-warning" role="alert">
              This accent may be hard to read on buttons ({accentContrast.toFixed(1)}:1 contrast, below the
              4.5:1 minimum).
            </p>
          )}
        </div>
      )}
    </section>
  );
}
